import React, { useState, useEffect } from 'react';
import { Card, Typography, Select, Switch, Row, Col, Tag, Rate, Avatar, Button, Modal, Form, Input, DatePicker, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../services/api';
import { absoluteUploadUrl } from '../../utils/uploadUrl';
import CommunityWelcomeModal from './CommunityWelcomeModal';
import { teamIcon, playerIcon } from '../../components/community/mapIcons';

const { Title } = Typography;
const { Option } = Select;

// Fix para ícones default do Leaflet (usado só como fallback do L.Icon.Default)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Observa o zoom atual do mapa pra trocar o ícone do jogador por foto de perfil (zoom >= 14)
const ZoomWatcher = ({ onZoomChange }) => {
  useMapEvents({ zoom: (e) => onZoomChange(e.target.getZoom()) });
  return null;
};

const CommunityMap = () => {
  const [mapData, setMapData] = useState({ teams: [], players: [] });
  const [ads, setAds] = useState([]); // v3: banners tipo pin no mapa
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    type: 'both',
    skill_level: null,
    position: null,
    showTeams: true,
    showPlayers: true
  });
  const [mapBounds, setMapBounds] = useState(null);
  const [inviteModal, setInviteModal] = useState({ visible: false, player: null });
  const [userType, setUserType] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(9);

  const skillLevels = [
    { value: 'iniciante', label: 'Iniciante' },
    { value: 'recreativo', label: 'Recreativo' },
    { value: 'intermediario', label: 'Intermediário' },
    { value: 'avancado', label: 'Avançado' },
    { value: 'semi_pro', label: 'Semi-pro' }
  ];

  const positions = [
    { value: 'goleiro', label: 'Goleiro' },
    { value: 'zagueiro', label: 'Zagueiro' },
    { value: 'lateral', label: 'Lateral' },
    { value: 'volante', label: 'Volante' },
    { value: 'meia', label: 'Meia' },
    { value: 'atacante', label: 'Atacante' },
    { value: 'qualquer', label: 'Qualquer' }
  ];

  const fetchMapData = async (bounds) => {
    if (!bounds) return;
    
    setLoading(true);
    try {
      const params = {
        bounds: JSON.stringify(bounds),
        type: filters.type,
        ...(filters.skill_level && { skill_level: filters.skill_level }),
        ...(filters.position && { position: filters.position })
      };

      const token = localStorage.getItem('community_token') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await api.get('/community/map/data', { params, headers });
      setMapData(response.data);
    } catch (error) {
      console.error('Erro ao buscar dados do mapa:', error);
    }
    setLoading(false);
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const params = {
        type: filters.type,
        ...(filters.skill_level && { skill_level: filters.skill_level }),
        ...(filters.position && { position: filters.position })
      };

      const token = localStorage.getItem('community_token') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await api.get('/community/map/data', { params, headers });
      setMapData(response.data);
    } catch (error) {
      console.error('Erro ao buscar dados iniciais:', error);
    }
    setLoading(false);
  };

  const handleMapMove = (map) => {
    const bounds = map.getBounds();
    const boundsObj = {
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest()
    };
    setMapBounds(boundsObj);
    fetchMapData(boundsObj);
  };

  useEffect(() => {
    if (mapBounds) {
      fetchMapData(mapBounds);
    } else {
      fetchInitialData();
    }
  }, [filters]);

  // v3: fetch banners tipo pin no mapa (segmentados por lat/lng do centro)
  useEffect(() => {
    (async () => {
      try {
        const c = mapBounds
          ? { lat: (mapBounds.north + mapBounds.south) / 2, lng: (mapBounds.east + mapBounds.west) / 2 }
          : {};
        const r = await api.get('/community/map/ads', { params: c });
        setAds(r.data.ads || []);
        // Grava impression pra cada ad exibido
        (r.data.ads || []).forEach(ad => {
          api.post(`/community/map/ads/${ad.id}/event`, { type: 'impression' }).catch(() => {});
        });
      } catch { /* silencioso — ads nao pode quebrar mapa */ }
    })();
  }, [mapBounds]);

  useEffect(() => {
    fetchInitialData();
    // Verificar tipo de usuário baseado na URL atual
    const currentPath = window.location.pathname;
    if (currentPath.includes('/player/')) {
      setUserType('player');
      // Se for jogador, filtrar apenas times por padrão
      setFilters(prev => ({ ...prev, type: 'teams', showPlayers: false }));
    } else {
      setUserType('organizer');
    }
  }, []);

  const getSkillColor = (level) => {
    const colors = {
      iniciante: 'green',
      recreativo: 'blue',
      intermediario: 'orange',
      avancado: 'red',
      semi_pro: 'purple'
    };
    return colors[level] || 'default';
  };

  const handleInvitePlayer = async (values) => {
    setLoading(true);
    try {
      await api.post('/community/invites/invite-player', {
        player_id: inviteModal.player.id,
        message: values.message,
        game_date: values.game_date.format('YYYY-MM-DD'),
        game_time: values.game_time
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      message.success('Convite enviado!');
      setInviteModal({ visible: false, player: null });
    } catch (error) {
      message.error('Erro ao enviar convite');
    }
    setLoading(false);
  };

  const handleApplyToTeam = async (team, gameId = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('community_token');
      // Se nao veio gameId da UI, tenta buscar proximo aberto (fallback compat).
      let finalGameId = gameId;
      if (!finalGameId) {
        try {
          const r = await api.get(`/community/games/team/${team.id}/next-open`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          finalGameId = r.data?.id || null;
        } catch { finalGameId = null; }
      }

      const body = finalGameId
        ? { team_id: team.id, message: `Gostaria de jogar no ${team.name}`, game_id: finalGameId, kind: 'GAME' }
        : { team_id: team.id, message: `Quero conhecer o ${team.name}`, kind: 'GENERIC' };

      await api.post('/community/invites/apply-to-team', body, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success(finalGameId ? 'Bora! Candidatura mandada pro jogo.' : 'Bora! Candidatura enviada.');
      fetchInitialData(); // Recarregar dados
    } catch (error) {
      const msg = error?.response?.data?.message || 'Erro ao enviar candidatura';
      message.error(msg);
    }
    setLoading(false);
  };

  return (
    <div style={{ padding: window.innerWidth < 768 ? '8px' : '20px' }}>
      <Card size={window.innerWidth < 768 ? 'small' : 'default'}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: window.innerWidth < 768 ? 8 : 16, 
          flexWrap: 'wrap', 
          gap: window.innerWidth < 768 ? 4 : 8 
        }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => window.history.back()}
            size={window.innerWidth < 768 ? 'small' : 'default'}
          >
            {window.innerWidth < 768 ? '' : 'Voltar'}
          </Button>
          <Title 
            level={window.innerWidth < 768 ? 4 : 3} 
            style={{ 
              margin: 0, 
              fontSize: window.innerWidth < 768 ? '16px' : '18px' 
            }}
          >
            {window.innerWidth < 768 ? 'Mapa' : 'Mapa da Comunidade'}
          </Title>
        </div>
        
        <Row 
          gutter={window.innerWidth < 768 ? [8, 8] : [16, 16]} 
          style={{ marginBottom: window.innerWidth < 768 ? 24 : 16 }}
        >
          <Col xs={12} sm={12} md={6}>
            <Select
              placeholder={window.innerWidth < 768 ? 'Nível' : 'Nível técnico'}
              style={{ width: '100%' }}
              size={window.innerWidth < 768 ? 'small' : 'default'}
              allowClear
              value={filters.skill_level}
              onChange={(value) => setFilters({ ...filters, skill_level: value })}
            >
              {skillLevels.map(level => (
                <Option key={level.value} value={level.value}>{level.label}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Select
              placeholder="Posição"
              style={{ width: '100%' }}
              size={window.innerWidth < 768 ? 'small' : 'default'}
              allowClear
              value={filters.position}
              onChange={(value) => setFilters({ ...filters, position: value })}
            >
              {positions.map(pos => (
                <Option key={pos.value} value={pos.value}>{pos.label}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 6,
              fontSize: window.innerWidth < 768 ? 12 : 14,
              padding: window.innerWidth < 768 ? '8px 0' : '0'
            }}>
              <span>Times:</span>
              <Switch
                size={window.innerWidth < 768 ? 'small' : 'default'}
                checked={filters.showTeams}
                onChange={(checked) => setFilters({ 
                  ...filters, 
                  showTeams: checked,
                  type: checked && filters.showPlayers ? 'both' : checked ? 'teams' : 'players'
                })}
              />
            </div>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 6,
              fontSize: window.innerWidth < 768 ? 12 : 14,
              padding: window.innerWidth < 768 ? '8px 0' : '0'
            }}>
              <span>Jogadores:</span>
              <Switch
                size={window.innerWidth < 768 ? 'small' : 'default'}
                checked={filters.showPlayers}
                onChange={(checked) => setFilters({ 
                  ...filters, 
                  showPlayers: checked,
                  type: filters.showTeams && checked ? 'both' : checked ? 'players' : 'teams'
                })}
              />
            </div>
          </Col>
        </Row>

        <div style={{ height: window.innerWidth < 768 ? '400px' : '600px', width: '100%' }}>
          <MapContainer
            center={[-23.2, -46.5]} // Centro entre SP e Bragança
            zoom={9}
            style={{ height: '100%', width: '100%' }}
            whenReady={(map) => handleMapMove(map.target)}
            onMoveEnd={(e) => handleMapMove(e.target)}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            <ZoomWatcher onZoomChange={setCurrentZoom} />

            {filters.showTeams && mapData.teams.map(team => (
              <Marker
                key={`team-${team.id}`}
                position={[team.latitude, team.longitude]}
                icon={teamIcon({ openGames: team.open_games_week || 0, logoUrl: absoluteUploadUrl(team.logo_url) })}
              >
                <Popup>
                  <div style={{ minWidth: window.innerWidth < 768 ? 150 : 200 }}>
                    <Title level={5}>{team.name}</Title>
                    <p><strong>Local:</strong> {team.neighborhood}, {team.city}</p>
                    <p><strong>Nível:</strong> <Tag color={getSkillColor(team.skill_level)}>{team.skill_level}</Tag></p>
                    {team.guest_price && <p><strong>Valor avulso:</strong> R$ {team.guest_price}</p>}
                    {team.game_schedule && team.game_schedule.length > 0 && (
                      <div>
                        <p><strong>Dias de jogos:</strong></p>
                        {team.game_schedule.map((schedule, index) => (
                          <p key={index} style={{ marginLeft: 10, marginBottom: 4 }}>{schedule}</p>
                        ))}
                      </div>
                    )}
                    {team.average_rating > 0 && (
                      <p><strong>Avaliação:</strong> <Rate disabled value={team.average_rating} /></p>
                    )}
                    {userType === 'player' && (
                      <div>
                        {team.last_application_status && (
                          <p><strong>Última candidatura:</strong>{' '}
                            <Tag color={
                              team.last_application_status === 'PENDING' ? 'orange' :
                              team.last_application_status === 'ACCEPTED' ? 'green' : 'red'
                            }>
                              {team.last_application_status === 'PENDING' ? 'Pendente' :
                               team.last_application_status === 'ACCEPTED' ? 'Aceita' : 'Recusada'}
                            </Tag>
                          </p>
                        )}
                        {/* Proximos jogos abertos: 1 botao por jogo, direto na candidatura */}
                        {team.open_games && team.open_games.length > 0 && (
                          <div style={{ marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Próximas peladas:</div>
                            {team.open_games.map(g => (
                              <div key={g.id} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                gap: 6, marginBottom: 6, fontSize: 12,
                              }}>
                                <span>⚽ {g.date_of_game}</span>
                                <Button
                                  type="primary" size="small"
                                  disabled={!team.can_apply || loading}
                                  onClick={() => handleApplyToTeam(team, g.id)}
                                >
                                  Bora!
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                        {(!team.open_games || team.open_games.length === 0) && team.can_apply && (
                          <Button
                            size="small"
                            style={{ marginTop: 8 }}
                            onClick={() => handleApplyToTeam(team, null)}
                          >
                            Ficar de olho
                          </Button>
                        )}
                        {!team.can_apply && (
                          <Button size="small" disabled style={{ marginTop: 8 }}>
                            {team.last_application_status === 'PENDING' ? 'Pendente com esse time' : 'Já mandou candidatura'}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}

            {filters.showPlayers && mapData.players.map(player => (
              <Marker
                key={`player-${player.id}`}
                position={[player.latitude, player.longitude]}
                icon={playerIcon({
                  isGoalkeeper: player.preferred_position === 'goleiro',
                  photoUrl: player.profile_photo,
                  zoom: currentZoom,
                })}
              >
                <Popup>
                  <div style={{ minWidth: window.innerWidth < 768 ? 150 : 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                      <Avatar src={player.profile_photo} style={{ marginRight: 8 }}>
                        {player.name.charAt(0)}
                      </Avatar>
                      <Title level={5} style={{ margin: 0 }}>{player.name}</Title>
                    </div>
                    <p><strong>Local:</strong> {player.neighborhood}, {player.city}</p>
                    <p><strong>Posição:</strong> {player.preferred_position}</p>
                    <p><strong>Nível:</strong> <Tag color={getSkillColor(player.skill_level)}>{player.skill_level}</Tag></p>
                    {player.preferred_position === 'goleiro' && player.game_price && (
                      <p><strong>Valor por jogo:</strong> R$ {player.game_price}</p>
                    )}
                    {player.average_rating > 0 && (
                      <p><strong>Avaliação:</strong> <Rate disabled value={player.average_rating} /></p>
                    )}
                    {userType === 'organizer' && (
                      <Button 
                        type="primary" 
                        size="small" 
                        style={{ marginTop: 8 }}
                        onClick={() => setInviteModal({ visible: true, player })}
                      >
                        Convidar
                      </Button>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* v3: pins de ads no mapa — logo redonda com borda dourada */}
            {ads.map(ad => (
              <Marker
                key={`ad-${ad.id}`}
                position={[Number(ad.map_lat), Number(ad.map_lng)]}
                icon={L.divIcon({
                  className: 'fz-map-icon',
                  html: `<div style="width:44px;height:44px;border-radius:50%;background:#fff;border:3px solid #FFB800;box-shadow:0 2px 8px rgba(0,0,0,0.3);overflow:hidden;display:flex;align-items:center;justify-content:center">
                    <img src="${ad.logo_url || ad.image_mobile_url || ad.image_desktop_url}" style="width:100%;height:100%;object-fit:cover" onerror="this.style.display='none'"/>
                  </div>
                  <div style="position:absolute;top:-8px;right:-8px;background:#FFB800;color:#fff;font-size:10px;font-weight:700;padding:2px 5px;border-radius:8px">AD</div>`,
                  iconSize: [44, 44],
                  iconAnchor: [22, 22],
                })}
              >
                <Popup>
                  <div style={{ minWidth: 220, maxWidth: 280 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 10 }}>
                      {ad.logo_url && <img src={ad.logo_url} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} />}
                      <div>
                        <Title level={5} style={{ margin: 0 }}>{ad.pin_title || ad.title}</Title>
                        <Tag color="gold" style={{ marginTop: 2 }}>Patrocinado</Tag>
                      </div>
                    </div>
                    {ad.pin_description && (
                      <p style={{ fontSize: 13, color: '#555', margin: '8px 0' }}>{ad.pin_description}</p>
                    )}
                    {ad.target_url && (
                      <Button type="primary" block size="small"
                        onClick={() => {
                          api.post(`/community/map/ads/${ad.id}/event`, { type: 'click' }).catch(() => {});
                          window.open(ad.target_url, '_blank', 'noopener,noreferrer');
                        }}>
                        Visitar
                      </Button>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Modal de Convite */}
        <Modal
          title={`Convidar ${inviteModal.player?.name}`}
          open={inviteModal.visible}
          onCancel={() => setInviteModal({ visible: false, player: null })}
          footer={null}
        >
          <Form onFinish={handleInvitePlayer} layout="vertical">
            <Form.Item name="game_date" label="Data do Jogo" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="game_time" label="Horário">
              <Input placeholder="19:00" />
            </Form.Item>
            <Form.Item name="message" label="Mensagem">
              <Input.TextArea rows={3} placeholder="Convite para jogar..." />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                Enviar Convite
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
      <CommunityWelcomeModal />
    </div>
  );
};

export default CommunityMap;